/**
 * Testimonials Carousel Section
 * Implements main testimonial carousel with nested image carousels
 * Uses Swiper centeredSlides with JS-controlled slidesPerView
 * Supports Shopify customizer refresh events
 */

class TestimonialsCarousel {
  constructor(element) {
    this.element = element;
    this.mainSwiper = null;
    this.imageSwipers = [];
    this._initRetryTimer = null;
    this._initCycleTimer = null; //inicia thubnailq
    this._imageEndTimer = null;
    this._imageAutoplayTimer = null;
    this._imageTransitionTimer = null; //iocional
    this._currentCycleRealIndex = -1;
    this._slideChangeFallbackTimer = null;
    this._inView = false;
    this._io = null;
    this._onVisibility = null;
    this._autoAdvancing = false;
    this.readSettings();
    this.initialize();
  }

  readSettings() {
    const el = this.element;
    this.slideSpeed = Math.round((parseFloat(el.dataset.slideSpeed) || 0.6) * 1000);
    this.imageTransitionSpeed = Math.round((parseFloat(el.dataset.imageTransitionSpeed) || 2) * 1000);
    this.imageAutoplayDelay = Math.round((parseFloat(el.dataset.imageAutoplayDelay) || 1.5) * 1000);
    this.imageAnimation = el.dataset.imageAnimation || 'fade';
    this.mainAutoplayEnabled = el.dataset.mainAutoplay !== 'false';
    this.mainAutoplayDelay = Math.round((parseFloat(el.dataset.mainAutoplayDelay) || 4) * 1000);
    this.slidesPerPage = Math.max(1, parseInt(el.dataset.slidesPerPage || '3', 10));
    this.counterDuration = Math.round((parseFloat(el.dataset.counterDuration) || 1.8) * 1000);
    this.navigationType = el.dataset.navigationType || 'dots';
  }

  initialize() {
    if (typeof Swiper === 'undefined') {
      this._initRetryTimer = setTimeout(() => this.initialize(), 100);
      return;
    }

    const mainSwiperEl = this.element.querySelector('.testimonials-carousel__main-swiper');
    if (!mainSwiperEl) {
      this._initRetryTimer = setTimeout(() => this.initialize(), 100);
      return;
    }

    // Init main carousel FIRST, then image carousels
    this.initializeMainCarousel();
    this.initializeImageCarousels();
    this.initMetricsAnimation();

    // Drive the thumbnail cycle from viewport visibility (IntersectionObserver) so it
    // starts reliably on mobile (iOS/Android). A one-shot load-time timer can be
    // throttled or fire while the section is far below the fold and never recover.
    this.setupAutoplayVisibility();
  }

  setupAutoplayVisibility() {
    const target = this.element.querySelector('.testimonials-carousel__main') || this.element;

    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const nowVisible = entry.isIntersecting;
        // Only react to enter/leave edges so scroll jitter doesn't keep resetting the cycle
        if (nowVisible === this._inView) return;
        this._inView = nowVisible;
        if (nowVisible) {
          if (this.mainSwiper && !this.mainSwiper.destroyed) this.updateImageAutoplay(this.mainSwiper);
        } else {
          this.stopImageCycle();
        }
      }, { threshold: 0.1 });
      this._io.observe(target);
    } else {
      // Fallback for browsers without IntersectionObserver: just start
      this._inView = true;
      if (this.mainSwiper && !this.mainSwiper.destroyed) this.updateImageAutoplay(this.mainSwiper);
    }

    // Pause when the tab/app is backgrounded (mobile lock/app-switch, bfcache) and
    // resume when it comes back while still in view.
    this._onVisibility = () => {
      if (document.hidden) {
        this.stopImageCycle();
      } else if (this._inView && this.mainSwiper && !this.mainSwiper.destroyed) {
        this.updateImageAutoplay(this.mainSwiper);
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);
  }

  initMetricsAnimation() {
    const metrics = this.element.querySelectorAll('.testimonials-carousel__metric-value[data-target]');
    if (!metrics.length) return;

    // Parse target value and suffix for each metric, reset display to 0
    metrics.forEach((el) => {
      const raw = el.dataset.target;
      const match = raw.match(/^(\d+(?:\.\d+)?)(.*)$/);
      if (!match) return;
      el._counterTarget = parseFloat(match[1]);
      el._counterSuffix = match[2];
      el.textContent = '0' + el._counterSuffix;
    });

    const container = this.element.querySelector('.testimonials-carousel__metrics-container');
    if (!container) return;

    this._counterGen = 0;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          observer.disconnect();
          this.runCounters(metrics, ++this._counterGen);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(container);
  }

  runCounters(metrics, gen) {
    const metricsArr = Array.from(metrics);
    const maxTarget = Math.max(
      ...metricsArr.map((el) => (/** @type {any} */ (el))._counterTarget ?? 0).filter((v) => !isNaN(v))
    );
    if (maxTarget <= 0) return;

    // Ensure at least 16ms per integer so no number is skipped
    const duration = Math.max(this.counterDuration, maxTarget * 16);
    let startTime = null;

    const tick = (now) => {
      if (gen !== this._counterGen) return;
      if (!startTime) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      metricsArr.forEach((el /** @type {HTMLElement & {_counterTarget?: number, _counterSuffix?: string}} */) => {
        if (el._counterTarget === undefined) return;
        const value = progress < 1 ? Math.floor(progress * el._counterTarget) : el._counterTarget;
        el.textContent = value + el._counterSuffix;
      });

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }

  initializeImageCarousels() {
    // Query ALL image swipers including loop-cloned ones
    const imageCarousels = this.element.querySelectorAll('.testimonials-carousel__image-swiper');

    imageCarousels.forEach((carousel) => {
      // Skip if already initialized by Swiper
      if (carousel.swiper) return;

      const slides = carousel.querySelectorAll('.swiper-slide');
      if (slides.length === 0) return;

      // Build effect config based on animation setting
      const effectConfig = {};
      if (this.imageAnimation === 'fade') {
        effectConfig.effect = 'fade';
        effectConfig.fadeEffect = { crossFade: true };
      } else if (this.imageAnimation === 'creative') {
        effectConfig.effect = 'creative';
        effectConfig.creativeEffect = {
          prev: { scale: 0.85, opacity: 0 },
          next: { scale: 0.85, opacity: 0 },
        };
      }
      // 'slide' is the default Swiper effect, no extra config needed

      const swiper = new Swiper(carousel, {
        slidesPerView: 1,
        loop: false,
        ...effectConfig,
        speed: this.imageTransitionSpeed,
        cssEase: 'cubic-bezier(0, 0, 0.2, 1)',
        on: {
          slideChange: (swiperInstance) => {
            // Sincronizar thumbnails cuando el usuario cambia imagen manualmente
            const card = carousel.closest('.testimonials-carousel__card');
            if (card) this.updateCardThumbnails(card, swiperInstance.activeIndex);
          },
        },
      });

      this.imageSwipers.push(swiper);

      // Init thumbnails scoped to this specific card
      const card = carousel.closest('.testimonials-carousel__card');
      if (card) this.initializeCardThumbnails(card, swiper);
    });
  }

  initializeCardThumbnails(card, imageSwiper) {
    const thumbnails = card.querySelectorAll('.testimonials-carousel__thumbnail');

    thumbnails.forEach((thumbnail, thumbIndex) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        imageSwiper.slideTo(thumbIndex);
        this.updateCardThumbnails(card, thumbIndex);
      });
    });

    // Set initial active thumbnail
    this.updateCardThumbnails(card, 0);
  }

  updateCardThumbnails(card, activeIndex) {
    const thumbnails = card.querySelectorAll('.testimonials-carousel__thumbnail');
    thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle('active', index === activeIndex);
    });

    const activeThumb = thumbnails[activeIndex];
    const timeframeEl = card.querySelector('.testimonials-carousel__timeframe');
    if (timeframeEl && activeThumb) {
      const label = activeThumb.dataset.label || timeframeEl.dataset.labelDefault || '';
      timeframeEl.textContent = label ? ` AT ${label}` : '';
    }
  }

  initializeMainCarousel() {
    const mainCarousel = this.element.querySelector('.testimonials-carousel__main-swiper');
    if (!mainCarousel) return;

    const swiperWrapper = mainCarousel.querySelector(':scope > .swiper-wrapper');
    const originalSlides = swiperWrapper ? Array.from(swiperWrapper.querySelectorAll(':scope > .swiper-slide')) : [];
    const originalCount = originalSlides.length;
    if (originalCount === 0) return;

    // Calcular cuántas slides son visibles en el viewport actual (incluyendo resoluciones grandes)
    const slideWidth = 300;
    const gap = 24;
    const visiblePerView = Math.ceil(window.innerWidth / (slideWidth + gap)) + 2;
    const minNeeded = Math.max(14, visiblePerView * 3);
    if (swiperWrapper.querySelectorAll(':scope > .swiper-slide').length < minNeeded) {
      let cloneRound = 0;
      while (swiperWrapper.querySelectorAll(':scope > .swiper-slide').length < minNeeded) {
        originalSlides.forEach(slide => {
          const clone = slide.cloneNode(true);
          clone.setAttribute('data-clone', 'true');
          swiperWrapper.appendChild(clone);
        });
        cloneRound++;
        if (cloneRound > 10) break; // safety
      }
    }

    // Store for pagination
    this.originalSlideCount = originalCount;

    // Arrows (same as the reviews slider) — only when navigation type is "arrows".
    const useArrows = this.navigationType === 'arrows';
    const prevEl = useArrows ? this.element.querySelector('.testimonials-carousel__button-prev') : null;
    const nextEl = useArrows ? this.element.querySelector('.testimonials-carousel__button-next') : null;

    this.mainSwiper = new Swiper(mainCarousel, {
      slidesPerView: 'auto',
      spaceBetween: 24,
      centeredSlides: true,
      initialSlide: 0,
      loop: true,
      resizeObserver: true,
      speed: this.slideSpeed,
      cssEase: 'cubic-bezier(0, 0, 0.2, 1)',
      grabCursor: true,
      slideToClickedSlide: true,
      pagination: false, // we handle pagination manually
      ...(useArrows && prevEl && nextEl ? { navigation: { prevEl: prevEl, nextEl: nextEl } } : {}),
      ...(this.mainAutoplayEnabled ? {
        autoplay: {
          delay: this.mainAutoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
      } : {}),
      on: {
        init: (swiper) => {
          // The visibility-driven image cycle advances the main carousel itself, so stop
          // native autoplay — otherwise it would run while the section is off-screen.
          if (swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();
          // Wait for Swiper to apply swiper-slide-visible classes before building pagination
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (this.navigationType !== 'arrows') this.buildPagination(swiper, originalCount);
              this.correctCenter(swiper);
            });
          });

          // Add click handlers to all slides for centering
          swiper.slides.forEach((slide, index) => {
            slide.addEventListener('click', (e) => {
              // Don't center if clicking thumbnail
              if (e.target.closest('.testimonials-carousel__thumbnail')) return;
              // Get the real slide index (accounts for loop duplicates)
              const realIndex = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
              if (!isNaN(realIndex)) {
                swiper.slideToLoop(realIndex, 600);
              } else {
                swiper.slideTo(index, 600);
              }
            });
          });
        },
        slideChange: (swiper) => {
          this.updatePagination(swiper.realIndex % originalCount);
          if (this._autoAdvancing) return;
          if (swiper.realIndex % originalCount !== this._currentCycleRealIndex) {
            this.stopImageCycle();
            // iOS Safari fallback: slideChangeTransitionEnd may not fire after an arrow tap.
            // If it does fire it will clear this timer; if it doesn't, this restarts the cycle.
            if (this._slideChangeFallbackTimer) clearTimeout(this._slideChangeFallbackTimer);
            this._slideChangeFallbackTimer = setTimeout(() => {
              this._slideChangeFallbackTimer = null;
              if (this._autoAdvancing || !this.mainSwiper || this.mainSwiper.destroyed) return;
              const rect = this.element.getBoundingClientRect();
              if (rect.bottom > 0 && rect.top < window.innerHeight) {
                this._inView = true;
                this.updateImageAutoplay(this.mainSwiper);
              }
            }, this.slideSpeed + 120);
          }
        },
        slideChangeTransitionEnd: (swiper) => {
          this.correctCenter(swiper);
          if (this._autoAdvancing) return;
          // Cancel the iOS fallback timer — slideChangeTransitionEnd fired normally.
          if (this._slideChangeFallbackTimer) {
            clearTimeout(this._slideChangeFallbackTimer);
            this._slideChangeFallbackTimer = null;
          }
          const newRealIndex = swiper.realIndex % originalCount;
          if (newRealIndex !== this._currentCycleRealIndex) {
            const rect = this.element.getBoundingClientRect();
            if (rect.bottom > 0 && rect.top < window.innerHeight) {
              this._inView = true;
              this.updateImageAutoplay(swiper);
            }
          }
        },
        click: (swiper, event) => {
          if (event.target.closest('.testimonials-carousel__thumbnail')) return;
        },
      },
      breakpoints: {
        320: {
          spaceBetween: 12,
        },
        768: {
          spaceBetween: 16,
        },
        1024: {
          spaceBetween: 24,
        },
      },
    });

  }

  buildPagination(swiper, count) {
    const paginationEl = this.element.querySelector('.testimonials-carousel__pagination');
    if (!paginationEl) return;
    paginationEl.innerHTML = '';

    const slidesPerPage = this.slidesPerPage || 3;
    this._slidesPerPage = slidesPerPage;

    const pageCount = Math.ceil(count / slidesPerPage);

    for (let i = 0; i < pageCount; i++) {
      const bullet = document.createElement('span');
      bullet.className = 'testimonials-carousel__pagination-bullet';
      if (i === 0) bullet.classList.add('active');
      bullet.addEventListener('click', () => {
        swiper.slideToLoop(i * slidesPerPage, 600);
      });
      paginationEl.appendChild(bullet);
    }
  }

  updatePagination(realIndex) {
    const slidesPerPage = this._slidesPerPage || 1;
    const pageIndex = Math.floor(realIndex / slidesPerPage);
    const bullets = this.element.querySelectorAll('.testimonials-carousel__pagination-bullet');
    bullets.forEach((b, i) => b.classList.toggle('active', i === pageIndex));
  }

  correctCenter(swiper) {
    // Measure actual misalignment between active slide center and container center
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return;

    const containerRect = swiper.el.getBoundingClientRect();
    const slideRect = activeSlide.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;
    const slideCenter = slideRect.left + slideRect.width / 2;
    const offset = slideCenter - containerCenter;

    // Only correct if the offset is noticeable (> 1px)
    if (Math.abs(offset) > 1) {
      const newTranslate = swiper.getTranslate() - offset;
      // Manipulate DOM directly to avoid triggering swiper.setTranslate() which
      // calls updateActiveIndex() → slideChange → stopImageCycle (kills cycle timer)
      swiper.wrapperEl.style.transitionDuration = '150ms';
      swiper.wrapperEl.style.transform = `translate3d(${newTranslate}px, 0px, 0px)`;
      swiper.translate = newTranslate;
    }
  }

  stopImageCycle() {
    if (this._imageEndTimer) { clearTimeout(this._imageEndTimer); this._imageEndTimer = null; }
    if (this._imageAutoplayTimer) { clearTimeout(this._imageAutoplayTimer); this._imageAutoplayTimer = null; }
    if (this._imageTransitionTimer) { clearTimeout(this._imageTransitionTimer); this._imageTransitionTimer = null; }
  }

  updateImageAutoplay(mainSwiper) {
    // Cancelar cualquier ciclo en curso
    this.stopImageCycle();

    // No animar mientras el carrusel no esté visible (evita timers colgados/throttling en mobile)
    if (!this._inView) return;

    // El ciclo de thumbnails controla el avance entre cards — detener autoplay nativo
    if (mainSwiper.autoplay && mainSwiper.autoplay.running) {
      mainSwiper.autoplay.stop();
    }

    const newRealIndex = mainSwiper.realIndex % (this.originalSlideCount || 1);
    this._currentCycleRealIndex = newRealIndex;

    const activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
    const activeImageSwiperEl = activeSlide?.querySelector('.testimonials-carousel__image-swiper');

    // Resetear a la primera imagen en todas las cards no activas
    this.element.querySelectorAll('.testimonials-carousel__image-swiper').forEach(el => {
      if (!el.swiper) return;
      if (el !== activeImageSwiperEl) {
        el.swiper.slideTo(0, 0);
        const card = el.closest('.testimonials-carousel__card');
        if (card) this.updateCardThumbnails(card, 0);
      }
    });

    // Iniciar ciclo manual desde la primera imagen en el card activo
    if (!activeImageSwiperEl || !activeImageSwiperEl.swiper) return;

    const imageSwiper = activeImageSwiperEl.swiper;
    const activeCard = activeImageSwiperEl.closest('.testimonials-carousel__card');

    imageSwiper.slideTo(0, 0);
    if (activeCard) this.updateCardThumbnails(activeCard, 0);

    this.startImageCycle(imageSwiper, activeCard);
  }

  startImageCycle(imageSwiper, card) {
    const totalImages = imageSwiper.slides.length;
    let currentIndex = 0;

    const showNext = () => {
      const nextIndex = currentIndex + 1;

      // All images of this card shown → advance to the next card AND restart its cycle
      // ourselves via a timer. We deliberately do NOT rely on slideChangeTransitionEnd to
      // restart, because on mobile (iOS/Android) that event + Swiper's loop re-fix can be
      // missed/duplicated, which previously left the next card stalled.
      if (nextIndex >= totalImages) {
        this._imageAutoplayTimer = setTimeout(() => {
          this._imageAutoplayTimer = null;
          if (!this._inView || !this.mainSwiper || this.mainSwiper.destroyed) return;
          this._autoAdvancing = true;
          this.mainSwiper.slideNext();
          this._imageTransitionTimer = setTimeout(() => {
            this._imageTransitionTimer = null;
            this._autoAdvancing = false;
            if (this._inView && this.mainSwiper && !this.mainSwiper.destroyed) {
              this.updateImageAutoplay(this.mainSwiper);
            }
          }, this.slideSpeed + 80);
        }, this.imageAutoplayDelay);
        return;
      }

      this._imageAutoplayTimer = setTimeout(() => {
        this._imageAutoplayTimer = null;
        if (!this._inView || !imageSwiper || imageSwiper.destroyed) return;

        currentIndex = nextIndex;
        imageSwiper.slideTo(currentIndex, this.imageTransitionSpeed);
        if (card) this.updateCardThumbnails(card, currentIndex);

        // Esperar a que la transición termine antes de programar el siguiente
        this._imageTransitionTimer = setTimeout(() => {
          this._imageTransitionTimer = null;
          showNext();
        }, this.imageTransitionSpeed);
      }, this.imageAutoplayDelay);
    };

    // Iniciar el ciclo tras el primer delay
    showNext();
  }

  destroy() {
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
    if (this._onVisibility) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
    if (this._initRetryTimer) {
      clearTimeout(this._initRetryTimer);
      this._initRetryTimer = null;
    }
    if (this._slideChangeFallbackTimer) {
      clearTimeout(this._slideChangeFallbackTimer);
      this._slideChangeFallbackTimer = null;
    }
    if (this._initCycleTimer) { //testing
      clearTimeout(this._initCycleTimer);
      this._initCycleTimer = null;
    }
    if (this._imageEndTimer) {
      clearTimeout(this._imageEndTimer);
      this._imageEndTimer = null;
    }
    if (this._imageAutoplayTimer) {
      clearTimeout(this._imageAutoplayTimer);
      this._imageAutoplayTimer = null;
    }
    if (this._imageTransitionTimer) { //opconal
      clearTimeout(this._imageTransitionTimer);
      this._imageTransitionTimer = null;
    }
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
    this.imageSwipers.forEach(swiper => {
      if (swiper) swiper.destroy(true, true);
    });
    this.imageSwipers = [];
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCarousels);
} else {
  initCarousels();
}

function initCarousels() {
  const sections = document.querySelectorAll('.section-testimonials-carousel');
  sections.forEach((section) => {
    if (!section.testimonialsCarousel) {
      section.testimonialsCarousel = new TestimonialsCarousel(section);
    }
  });
}

function initSection(container) {
  const section = container.querySelector
    ? container.querySelector('.section-testimonials-carousel') || container.closest('.section-testimonials-carousel')
    : null;
  if (!section) return;
  if (section.testimonialsCarousel) {
    section.testimonialsCarousel.destroy();
  }
  section.testimonialsCarousel = new TestimonialsCarousel(section);
}

function destroySection(container) {
  const section = container.querySelector
    ? container.querySelector('.section-testimonials-carousel') || container.closest('.section-testimonials-carousel')
    : null;
  if (section && section.testimonialsCarousel) {
    section.testimonialsCarousel.destroy();
    section.testimonialsCarousel = null;
  }
}

// Shopify Theme Editor events
document.addEventListener('shopify:section:load', (e) => {
  initSection(e.target);
});

document.addEventListener('shopify:section:unload', (e) => {
  destroySection(e.target);
});

document.addEventListener('shopify:section:select', (e) => {
  // Re-init if the section was just loaded (initial page render in editor)
  if (e.detail && e.detail.load) {
    initSection(e.target);
  }
});

document.addEventListener('shopify:block:select', (e) => {
  const section = e.target.closest('.section-testimonials-carousel');
  if (!section || !section.testimonialsCarousel) return;
  const swiper = section.testimonialsCarousel.mainSwiper;
  if (!swiper) return;
  // Find the slide index matching this block
  const slide = e.target.closest('.swiper-slide');
  if (slide) {
    const realIndex = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
    if (!isNaN(realIndex)) {
      swiper.slideToLoop(realIndex, 600);
    }
  }
});
